import {
    Accordion,
	AccordionDetails, AccordionSummary, Box, Button,
	Divider,
	ListItem, ListSubheader, Snackbar,
	Typography,
    makeStyles,
    withStyles,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { ExpandMore } from "@material-ui/icons";
import { HubConnection } from "@microsoft/signalr";
import { AssistantResponse } from "./assistantResponse";
import { AssistantMode, AssistantService, SubmitFeedbackRequest, SubmitFeedbackResponse } from "../../../../services/AssistantService";
import { Alert } from "@material-ui/lab";
import { AssistantGetStarted } from "./assistantGetStarted";
import { AssistantFeelingStuck } from "./assistantFeelingStuck";
import { AssistantAnalyze } from "./assistantAnalyze";

export const DrawerAccordion = withStyles({
	root: {
		margin: 0,
		color: "white",
		"&$expanded": {
			margin: 0,
		},
	},
	expanded: {},
})(Accordion);

export const DrawerSummary = withStyles({
	root: {
		minHeight: 56,
		"&:hover": {
			cursor: "pointer",
		},
		"&$expanded": {
			minHeight: 56,
		},
	},
	content: {
		"&$expanded": {
			margin: "12px 0",
		},
	},
	expanded: {},
})(AccordionSummary);

export const useStyles = makeStyles((theme) => ({
	promptDetails: {
		backgroundColor: "#EFEFEF",
		display: "block",
	},
	responseDetails: {
		display: "flex",
		padding: 0,
	},
}));

interface FeedbackArgs {
    assetId: string;
    topics?: string;
    scriptureReferences?: string;
}

export function AssistantSection(props: {
    sectionMode: AssistantMode;
	expand: boolean;
	onExpand: () => void;
	hubConnection: HubConnection;
	resetConnection: () => void;
}) {
	const { sectionMode, expand, onExpand, hubConnection, resetConnection } = props;
	const classes = useStyles();
    const responseEndRef = useRef<null | HTMLDivElement>(null);

	const [mode, setMode] = useState<"prompt" | "response">("prompt");
	const [response, setResponse] = useState("");
	const [responseStream, setResponseStream] = useState<signalR.ISubscription<string>>();
	const [responseComplete, setResponseComplete] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const finishedGenerating = responseComplete || cancelled;

    const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
    const [feedbackArgs, setFeedbackArgs] = useState<FeedbackArgs>();

    let title = "";
    let sectionColor = "";
    if (sectionMode === AssistantMode.GetStarted) {
        title = "Need help getting started?"
        sectionColor = "#5081FF";
    } else if (sectionMode === AssistantMode.FeelingStuck) {
        title = "Feeling stuck & need ideas?"
        sectionColor = "#4067CA";
    } else if (sectionMode === AssistantMode.Analyze) {
        title = "Sermon done? Let's analyze it."
        sectionColor = "#3250A0";
    }

    const scrollToBottom = () => responseEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

    useEffect(() => {
        scrollToBottom();
    }, [response])

	const reset = () => {
        setResponse("");
        setResponseComplete(false);
		setMode("prompt");

        if (cancelled) {
            responseStream?.dispose();
            setResponseStream(undefined);
        }

        setCancelled(false);
	};

    const cancelGeneration = () => {
        setCancelled(true);
        responseStream?.dispose();
        setResponseStream(undefined);
        resetConnection();
    }

	const triggerGeneration = (methodName: string, assetId: string, topics?: string, scriptureReferences?: string) => {
        const methodArgs: FeedbackArgs = {assetId, topics, scriptureReferences}
        setFeedbackArgs(methodArgs);

		const r = hubConnection
			.stream<string>(methodName, methodArgs)
			.subscribe({
				next: (message) => {
					setResponse((r) => r += message);
				},
				complete: () => {
					setResponseStream(undefined);
					setResponseComplete(true);
                    setTimeout(() => scrollToBottom(), 1000);
				},
				error: (err) => {
					if (err.message) {
						const parts = err.message.split("Exception: ");
						setResponse((r) => r + parts[1]);
					}
					else {
						setResponse("An unknown error has occurred")
					}
					setResponseStream(undefined);
				},
			});
		setResponseStream(r);
        setMode("response");
	};

    const triggerAlert = (message: string) => {
		setAlertMessage(message);
		setAlertOpen(true);
		setTimeout(() => setAlertOpen(false), 3000);
	}

    const submitFeedback = async (feedback: string | undefined, thumbsUp: boolean | undefined): Promise<SubmitFeedbackResponse> => {
        const request: SubmitFeedbackRequest = {
			assetId: feedbackArgs!.assetId,
			mode: sectionMode,
			topics: feedbackArgs!.topics,
			scriptureReferences: feedbackArgs!.scriptureReferences,
			assistantResponse: response,
			feedback,
			thumbsUp,
		}

		const feedbackResponse = await AssistantService.submitFeedback(request);
		if (feedbackResponse.error) {
            return { success: false, errorMessage: feedbackResponse.message };
		} else {
			return { success: true }
		}
    }

	return (
		<>
            <DrawerAccordion expanded={expand}>
                <ListSubheader style={{ padding: 0, color: "white", top: 65 }}>
                    <DrawerSummary
                        onClick={onExpand}
                        expandIcon={<ExpandMore />}
                        style={{ backgroundColor: sectionColor }}
                    >
                        <Typography>{title}</Typography>
                    </DrawerSummary>

                    {expand && mode === "response" && (
                        <Box paddingBottom={0}>
                            {finishedGenerating && 
                                <>
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(response);
                                            triggerAlert("Copied to clipboard!");
                                        }}
                                        color="primary"
                                        style={{ fontSize: 12, paddingBottom: 0 }}
                                    >
                                        Copy To Clipboard
                                    </Button>
                                    <Button
                                        onClick={reset}
                                        color="primary"
                                        style={{ fontSize: 12, paddingBottom: 0 }}
                                    >
                                        Start Over
                                    </Button>
                                </>
                            }
                            {!finishedGenerating &&
                                <Button
                                    onClick={cancelGeneration}
                                    color="primary"
                                    style={{ fontSize: 12, paddingBottom: 0 }}
                                >
                                    Stop generating
                                </Button>
                            }
                            <Divider />
                        </Box>
                    )}
                </ListSubheader>

                <ListItem style={{ padding: 0 }}>
                    <AccordionDetails className={mode === "prompt" ? classes.promptDetails : classes.responseDetails}>
                        {mode === "prompt" && (
                            <>
                                {sectionMode === AssistantMode.GetStarted &&
                                    <AssistantGetStarted onSubmit={(assetId: string, topics: string, scriptureReferences: string) => triggerGeneration("GenerateSermonIdeas", assetId, topics, scriptureReferences)} />
                                }
                                {sectionMode === AssistantMode.FeelingStuck &&
                                    <AssistantFeelingStuck onSubmit={(assetId: string) => triggerGeneration("GenerateSermonSuggestions", assetId)} />
                                }
                                {sectionMode === AssistantMode.Analyze &&
                                    <AssistantAnalyze onSubmit={(assetId: string) => triggerGeneration("GenerateSermonAnalysis", assetId)} />
                                }
                            </>
                        )}

                        {mode === "response" &&
                                <AssistantResponse response={response} responseComplete={responseComplete} onSubmit={submitFeedback}></AssistantResponse>
                        }
                    </AccordionDetails>
                </ListItem>

            </DrawerAccordion>
            <div ref={responseEndRef}></div>
            
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={alertOpen}
            >
                <Alert severity="success">
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
	);
}
